/**
 * Navigation helpers shared by every feature: open a person (sidebar or page), the command bar registry, and the
 * "focused contact" that single-key shortcuts act on.
 */
import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { create } from "zustand";
import type { Id } from "@hub/shared";
import { hostMode } from "@/engine/transport";

export interface CommandItem {
  id: string;
  title: string;
  keywords?: string[];
  shortcut?: string;
  group?: string;
  icon?: React.ComponentType<{ size?: number | string; className?: string }>;
  run: () => void;
  /** Hide from the palette but keep the shortcut live. */
  hidden?: boolean;
}

interface CommandBarState {
  open: boolean;
  initialQuery: string;
  registry: Map<string, CommandItem[]>;
  version: number;
  setOpen(open: boolean, initialQuery?: string): void;
  register(items: CommandItem[]): () => void;
  items(): CommandItem[];
}

let registrySeq = 0;

export const useCommandBarStore = create<CommandBarState>((set, get) => ({
  open: false,
  initialQuery: "",
  registry: new Map(),
  version: 0,
  setOpen(open, initialQuery = "") {
    set({ open, initialQuery });
  },
  register(items) {
    const key = `r${++registrySeq}`;
    get().registry.set(key, items);
    set({ version: get().version + 1 });
    return () => {
      get().registry.delete(key);
      set({ version: get().version + 1 });
    };
  },
  items() {
    const out: CommandItem[] = [];
    for (const list of get().registry.values()) out.push(...list);
    return out;
  },
}));

export function useCommandBar() {
  const setOpen = useCommandBarStore((s) => s.setOpen);
  const register = useCommandBarStore((s) => s.register);
  return {
    open: useCallback((query?: string) => setOpen(true, query), [setOpen]),
    close: useCallback(() => setOpen(false), [setOpen]),
    register,
  };
}

/** The contact the keyboard acts on (selected row, open page, or sidebar preview). */
interface SelectionState {
  focusedContactId: Id | null;
  sidebarContactId: Id | null;
  setFocused(id: Id | null): void;
  setSidebar(id: Id | null): void;
}
export const useSelection = create<SelectionState>((set) => ({
  focusedContactId: null,
  sidebarContactId: null,
  setFocused: (id) => set({ focusedContactId: id }),
  setSidebar: (id) => set({ sidebarContactId: id, focusedContactId: id ?? undefined }),
}));

const LIST_ROUTES = [/^\/$/, /^\/people/, /^\/group\//, /^\/search/, /^\/notes/, /^\/reminders/, /^\/archive/, /^\/matches/, /^\/network/, /^\/needs/, /^\/services/];

/**
 * Opens a contact. On list-style routes in web and desktop the person shows in the right sidebar; everywhere else
 * (and always in the extension's narrow panel) it navigates to the person page. Pass `{ page: true }` to force it.
 */
export function useOpenContact(): (contactId: Id, opts?: { page?: boolean }) => void {
  const navigate = useNavigate();
  const location = useLocation();
  const setSidebar = useSelection((s) => s.setSidebar);
  return useCallback(
    (contactId, opts) => {
      const compact = hostMode() === "extension" || (typeof window !== "undefined" && window.innerWidth < 900);
      const onList = LIST_ROUTES.some((r) => r.test(location.pathname));
      if (!opts?.page && !compact && onList) {
        setSidebar(contactId);
        return;
      }
      navigate(`/contact/${contactId}`);
    },
    [navigate, location.pathname, setSidebar],
  );
}

/** Shared nav store for things the layout and shortcuts both need. */
interface LayoutState {
  navCollapsed: boolean;
  shortcutsOpen: boolean;
  /** Send feedback: opened from the account menu, or the Mac app's Help menu. */
  feedbackOpen: boolean;
  toggleNav(): void;
  setShortcutsOpen(open: boolean): void;
  setFeedbackOpen(open: boolean): void;
}
export const useLayoutStore = create<LayoutState>((set) => ({
  navCollapsed: (() => {
    try {
      return localStorage.getItem("hub_nav_collapsed") === "1";
    } catch {
      return false;
    }
  })(),
  shortcutsOpen: false,
  feedbackOpen: false,
  toggleNav: () =>
    set((s) => {
      try {
        localStorage.setItem("hub_nav_collapsed", s.navCollapsed ? "0" : "1");
      } catch {
        // ignore
      }
      return { navCollapsed: !s.navCollapsed };
    }),
  setShortcutsOpen: (open) => set({ shortcutsOpen: open }),
  setFeedbackOpen: (open) => set({ feedbackOpen: open }),
}));
